/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import _ from "underscore";
import url from "../utils/url";
import StateMachine from "../utils/state-machine";

// every route gets a unique id
var routeIdCounter = 1;

function checkRouteMatch(routes, queryParams, remaining, destinationRoute) {
    let matched = false;

    for (let key in routes) {
        let route = routes[key];

        if (route.path && remaining.search(route.path) !== 0) {
            continue;
        }

        if (!queryMatches(route.query, queryParams)) {
            continue;
        }

        // remove the bit that matched
        remaining = remaining.replace(route.path, "");

        routes = route.children;
        matched = true;
        destinationRoute = route;

        break;
    }

    return {
        routes,
        destinationRoute,
        remaining,
        matched
    };
}

/**
 * @param {object|null} queryRequirements The query requirements provided by a route.
 * @param {object} queryParams A serialized URLSearchParams object.
 * @returns {boolean} True if there are no query param requirements or the query params match the requirements, else false.
 */
function queryMatches(queryRequirements, queryParams) {
    if (_.isEmpty(queryRequirements)) {
        return true;
    }

    function requirementIsInvalid(requirement, values) {
        for (let i = 0; i < values.length; i++) {
            if (_.isRegExp(requirement)) {
                return !requirement.test(values[i]) ? true : false;
            } else if (requirement !== values[i]) {
                return true;
            }
        }

        return false;
    }

    for (let key in queryRequirements) {
        let requirement = queryRequirements[key];
        let values = queryParams[key];

        if (!values) {
            return false;
        }

        if (requirementIsInvalid(requirement, values)) {
            return false;
        }
    }

    return true;
}

/**
 * @param {object} parent
 * @param {function} match
 * @returns {function} A function that can be used to register a route.
 */
function makeMatcher(parent, match) {
    return function(path, query, handler, fn) {
        // allow paths to be strings
        if (_.isString(path)) {
            path = new RegExp(path);
        }

        // validate query param requirements
        if (_.isObject(query) && !_.isArray(query)) {
            _.each(query, function(value) {
                if (!_.isRegExp(value) && !_.isString(value)) {
                    throw new Error(
                        "The values in the query param requirements object must be a string or RegExp."
                    );
                }
            });
        } else if (!_.isNull(query)) {
            throw new Error("Query param requirements should be an object or null.");
        }

        // although the state machine doesn't enforce these methods we do enforce them for the router states
        if (
            !handler ||
            !_.isFunction(handler.enter) ||
            !_.isFunction(handler.exit) ||
            !_.isFunction(handler.update)
        ) {
            throw new Error('A route handler must have "enter", "exit", and "update" functions.');
        }

        var routeId = routeIdCounter++;
        var route = {
            id: routeId,
            path: path,
            query: query,
            parent: parent,
            children: null
        };

        match(
            routeId,
            handler,
            function(match) {
                if (fn) {
                    fn(makeMatcher.call(this, route, match));
                }
            }.bind(this)
        );

        // index the route under the parent
        parent.children = parent.children || {};
        parent.children[routeId] = route;

        // also index by id
        this._routesById[routeId] = route;
    }.bind(this);
}

/**
 * The Router class used to create the public API.
 * @constructor
 */
function Router() {
    this._routesMap = {
        parent: null,
        path: /.*?/,
        children: {}
    };
    this._routesById = {};
    this._stateMachine = new StateMachine();
}

/**
 * Registers new routes.
 * @param {function} fn A function that will be called with one argument, another function
 *                      which can be used to register routes.
 * @example
 *
 *  var router = new Router();
 *
 *  router.map(function (match) {
 *
 *      match('/', null, {
 *          enter: function () {},
 *          update: function () {},
 *          exit: function () {}
 *      }, function (match) {
 *
 *          match('foo/', null, {
 *              enter: function () {},
 *              update: function () {},
 *              exit: function () {}
 *          });
 *
 *      });
 *
 *  });
 *
 */
Router.prototype.map = function(fn) {
    this._stateMachine.map(
        function(match) {
            fn(makeMatcher.call(this, this._routesMap, match));
        }.bind(this)
    );
};

/**
 * Updates the router with a new path and query params.
 * @param {string} path
 * @param {object} queryParams
 */
Router.prototype.update = function(path, queryParams) {
    let routes = this._routesMap.children;
    let remaining = path;
    let destinationRoute;

    queryParams = url.toURLSearchParams(queryParams).toJSON();

    while (routes) {
        let result = checkRouteMatch(routes, queryParams, remaining, destinationRoute);

        routes = result.routes;
        destinationRoute = result.destinationRoute;
        remaining = result.remaining;

        if (!result.matched) {
            routes = null;
        }
    }

    if (remaining) {
        throw new Error("No route matches: " + path + ". Remaining was " + remaining);
    }

    this._stateMachine.transitionTo(destinationRoute.id, path, queryParams);
};

var router = new Router();
router.Router = Router;
export default router;

